import React from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import * as submitActions from 'store/submit';

import AdminPageHeader from '../AdminPageHeader';
import FormBody from '../../../styled/workflow/FormBody';
import FormDescription from '../../../styled/workflow/FormDescription';
import DumbFormSection from '../../shared/DumbFormSection';
import FormField from '../Field';
import FormFieldRead from '../FieldRead';
import FormButtons from '../FormButtons';

import readableDate from 'utils/readableDate';
import {
  AcceptFormProps,
  AcceptLicenseData,
} from '../../../../dataaccess/requests/types';
import FormFieldReadLogos from '../FieldReadLogos';
import { ApplicationState } from '../../../../store';
import { LoadingView } from '../../shared/LoadingView';
import { AcceptLicenseSuccess } from '../../../container/AcceptLicenseSuccess';
import { AcceptLicenseCancelled } from '../../../container/AcceptLicenseCancelled';
import { getFrontEndUrlForPath } from '../../../../utils/workflowUtils';

const AcceptForm: React.FC<AcceptFormProps> = ({ details }) => {
  const { data, license, logos } = details;

  const dispatch = useDispatch();
  const {
    isSubmitSuccess,
    isSubmitting,
    logoGuidelines,
    submitError,
  } = useSelector((state: ApplicationState) => state.submit);

  const {
    branding_request_id,
    name,
    company_name,
    position,
    email,
    address,
    telephone,
    medium_format,
    brand_assets,
    other_assets,
    purpose_of_use,
    start_date,
    end_date,
    territory,
  } = data;

  const { register, handleSubmit, formState, errors, reset } = useForm<
    AcceptLicenseData
  >({
    submitFocusError: true,
    defaultValues: {
      company_name,
      name,
      position,
      agreeString: undefined,
      request_id: branding_request_id,
    },
  });

  const onSubmit = (data: AcceptLicenseData) => {
    if (!formState.isSubmitting) {
      data.request_id = branding_request_id;
      data.agree = data.agreeString === 'true';
      data.licenseUrl = getFrontEndUrlForPath(
        `/agreement/{request_id}`,
        branding_request_id
      );
      dispatch(submitActions.acceptLicense(data));
    }
  };

  if (isSubmitSuccess) {
    if (logoGuidelines) {
      return <AcceptLicenseSuccess />;
    } else {
      return <AcceptLicenseCancelled />;
    }
  }

  if (isSubmitting) {
    return <LoadingView />;
  }

  return (
    <>
      <AdminPageHeader
        title="Trade Mark Licence"
        subtitle="LICENCE FOR SHORT-TERM USE OF BBC TRADE MARKS ON SPECIFIC ITEMS"
      />

      <FormBody onSubmit={handleSubmit(onSubmit)}>
        <FormDescription>
          Please accept the terms and conditions and enter a date at the bottom
          of the page.
        </FormDescription>

        <DumbFormSection title={'1. LICENSEE'}>
          <FormFieldRead labelText="Company Name:" value={company_name} />
          <FormFieldRead labelText="Contact Name:" value={name} />
          <FormFieldRead labelText="Position:" value={position} />
          <FormFieldRead labelText="Address:" value={address} />
          <FormFieldRead labelText="Telephone:" value={telephone} />
          <FormFieldRead labelText="Email adress:" value={email} />
        </DumbFormSection>

        <DumbFormSection title="2. TRADE MARK(S)">
          {logos && (
            <FormFieldReadLogos
              labelText="BBC logos requested:"
              logos={logos}
            />
          )}
          {brand_assets && (
            <FormFieldRead
              labelText="BBC programme logos:"
              value={brand_assets}
            />
          )}
        </DumbFormSection>

        <DumbFormSection title={'3. DESCRIPTION OF THE ITEM(S)'}>
          <FormFieldRead labelText="" value={medium_format} />
        </DumbFormSection>

        <DumbFormSection title={'4. PURPOSE OF THE ITEM(S)'}>
          <FormFieldRead labelText="" value={purpose_of_use} />
        </DumbFormSection>

        <DumbFormSection title={'5. PERIOD OF USE'}>
          <FormFieldRead
            labelText="Start date:"
            value={readableDate(start_date) || ''}
          />
          <FormFieldRead
            labelText="End date:"
            value={readableDate(end_date) || ''}
          />
        </DumbFormSection>

        <DumbFormSection title={'6. TERRITORY'}>
          <FormFieldRead labelText="" value={territory} />
        </DumbFormSection>

        <DumbFormSection title={'7. MAIN AGREEMENT'}>
          <FormFieldRead labelText="" value={license?.main} />
          <FormFieldRead labelText="" value={license?.main2} />
        </DumbFormSection>

        <DumbFormSection title={'8. ADDITIONAL CONDITIONS'}>
          <FormFieldRead labelText="" value={license?.conditions} />
        </DumbFormSection>

        <DumbFormSection fullWidth title="">
          <div
            dangerouslySetInnerHTML={{
              __html:
                license?.agreement ||
                'something went wrong with the license text',
            }}
          />
        </DumbFormSection>

        <DumbFormSection title="Agreed by or on behalf of the Licensee:">
          <FormField
            inputType="input"
            fieldName="company_name"
            labelText="Company Name:"
            register={register}
            required={'Please provide a company name'}
            errors={errors}
          />

          <FormField
            inputType="input"
            fieldName="name"
            labelText="Name:"
            register={register}
            required="Please provide your name"
            errors={errors}
          />

          <FormField
            inputType="input"
            fieldName="position"
            labelText="Position:"
            register={register}
            required="Please provide your position"
            errors={errors}
          />

          <FormField
            labelText={'Date:'}
            fieldName={'date'}
            inputType={'date'}
            dateValue={undefined}
            register={register}
            errors={errors}
            required="Please provide a signature date"
          />

          <FormField
            inputType="agree_radio"
            fieldName="agreeString"
            labelText="Please choose:"
            options={[
              {
                value: true,
                label: 'I agree with the terms of the Licence',
              },
              {
                value: false,
                label:
                  'I do not accept the terms and conditions of the Licence and wish to cancel the request',
              },
            ]}
            register={register}
            required={'Please choose an option'}
            errors={errors}
          />
        </DumbFormSection>

        <DumbFormSection title="Agreed on behalf of the BBC:">
          <FormFieldRead labelText="Name:" value={details.license?.bbc_name} />

          <FormFieldRead
            labelText="Position:"
            value={details.license?.bbc_position}
          />

          <FormFieldRead
            labelText="Date:"
            dateValue={details.license?.bbc_date}
          />
        </DumbFormSection>
        <FormButtons disabled={formState.isSubmitting} onReset={reset} />
      </FormBody>
    </>
  );
};

export default AcceptForm;
